import { useCallback } from "react";
import { type ProvetContextType } from "../../../context/provetContext";
import { AppointmentTime, FormSelectItem, SelectedAppointmentTime } from "../../../types";
import { useCalendarState } from "./useCalendarState";
import { CalendarUpdateOptions } from "./useCalendarUpdater";
import { useRequestManager } from "./useRequestManager";

interface UseAppointmentPickerHandlersParams {
  calendarState: ReturnType<typeof useCalendarState>;
  updateSelectedDate: (newSelectedDate: Date, options?: CalendarUpdateOptions) => void;
  showTimesForSelectedVet: (selectedDate: Date) => void;
  provetContext: ProvetContextType;
  onSelectAppointmentTime?: (appointmentTime?: SelectedAppointmentTime) => void;
  currentAvailableVeterinarians: FormSelectItem[];
  requestManager: ReturnType<typeof useRequestManager>;
  showError: (duration: number, onTimeout?: () => void) => void;
}

export function useAppointmentPickerHandlers({
  calendarState,
  updateSelectedDate,
  showTimesForSelectedVet,
  provetContext,
  onSelectAppointmentTime,
  currentAvailableVeterinarians,
  requestManager,
  showError
}: UseAppointmentPickerHandlersParams) {
  const monthChanged = useCallback(
    (selectedMonth: Date) => {
      calendarState.setTimesStatus("loading");
      calendarState.setSelectedDate(selectedMonth);
      const currentDate = calendarState.selectedDate;
      const isPreviousMonth =
        selectedMonth.getFullYear() < currentDate.getFullYear() ||
        (selectedMonth.getFullYear() === currentDate.getFullYear() &&
          selectedMonth.getMonth() < currentDate.getMonth());
      updateSelectedDate(selectedMonth, {
        selectDayOfMonth: isPreviousMonth ? "last" : "first"
      });
    },
    [calendarState.selectedDate, updateSelectedDate]
  );

  const yearChanged = useCallback(
    (selectedYear: Date) => {
      calendarState.setTimesStatus("loading");
      const today = new Date();
      const firstDateOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

      // Year back never lands on a past month: it stops at this month, on today or the
      // first later day with times (observation 14)
      if (selectedYear < firstDateOfNextMonth) {
        calendarState.setSelectedDate(today);
        updateSelectedDate(today, { selectFirstAvailableDay: true });
        return;
      }

      calendarState.setSelectedDate(selectedYear);
      updateSelectedDate(selectedYear, { selectDayOfMonth: "first" });
    },
    [updateSelectedDate]
  );

  const dateChanged = useCallback(
    (selectedDate: Date) => {
      calendarState.setTimesStatus("loading");
      updateSelectedDate(selectedDate);
    },
    [updateSelectedDate]
  );

  const veterinarianSelected = useCallback(
    (formSelectItem: FormSelectItem) => {
      const vetId = parseInt(formSelectItem?.value?.toString() || "0");
      calendarState.setSelectedVetId(vetId);
      showTimesForSelectedVet(calendarState.selectedDate);
    },
    [calendarState.selectedDate, showTimesForSelectedVet]
  );

  const availableAppointmentTimeSelected = useCallback(
    async (availableAppointmentTime: AppointmentTime) => {
      const bufferTime =
        provetContext.bookingFlowState.formData.selectedDepartment
          ?.online_booking_buffer_time || 0;
      const currentTime = new Date();
      const appointmentTime = new Date(availableAppointmentTime.availableTime);
      if (appointmentTime > new Date(currentTime.getTime() + bufferTime * 60000)) {
        if (onSelectAppointmentTime) {
          const selectedVetId = calendarState.selectedVetIdRef.current;
          const selectedVetName =
            selectedVetId > 0
              ? currentAvailableVeterinarians.find(
                  v => v.value === selectedVetId.toString()
                )?.caption || ""
              : "";
          const selectedTimeArgs: SelectedAppointmentTime = {
            ...availableAppointmentTime,
            selectedVeterinarianId: selectedVetId,
            selectedVeterinarianName: selectedVetName
          };
          onSelectAppointmentTime(selectedTimeArgs);
        }
      } else {
        requestManager.clearTimer();
        showError(1500, () => {
          updateSelectedDate(calendarState.selectedDate);
        });
      }
    },
    [
      provetContext.bookingFlowState.formData.selectedDepartment,
      currentAvailableVeterinarians,
      onSelectAppointmentTime,
      requestManager.clearTimer,
      showError,
      updateSelectedDate,
      calendarState.selectedDate
    ]
  );

  return {
    monthChanged,
    yearChanged,
    dateChanged,
    veterinarianSelected,
    availableAppointmentTimeSelected
  };
}
